import { expect } from '@playwright/test';
import { test } from '@stencil/playwright';

// Set timeout for all tests in this file
test.setTimeout(30000);

test.describe('nylas-availability-picker', () => {
  async function waitForComponentReady(page) {
    // Wait for component to be hydrated
    await page.waitForSelector('nylas-availability-picker.hydrated', { timeout: 15000 });

    // Wait a bit for the component to fully initialize
    await page.waitForTimeout(1000);
  }

  test('should render and be hydrated', async ({ page }) => {
    await page.setContent(
      `<form id="availability-picker">
        <nylas-availability-picker name="availability"></nylas-availability-picker>
      </form>`,
    );

    const component = page.locator('nylas-availability-picker');
    await expect(component).toBeVisible();
    await expect(component).toHaveClass(/hydrated/);
  });

  test('should render availability days', async ({ page }) => {
    await page.setContent(`<form id="availability-picker">
      <nylas-availability-picker name="availability"></nylas-availability-picker>
    </form>`);

    await waitForComponentReady(page);

    // Use shadow DOM selector
    const availabilityDays = page.locator('nylas-availability-picker >> .availability-day');
    await expect(availabilityDays).toHaveCount(7);
  });

  test('should have correct default checked days', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await waitForComponentReady(page);

    // Use shadow DOM selector
    const checkedDays = page.locator('nylas-availability-picker >> .availability-day input[type="checkbox"]:checked');
    await expect(checkedDays).toHaveCount(5);
  });

  test('should correctly mark specific days in default state', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);
    await waitForComponentReady(page);

    // Get all day checkboxes
    const dayCheckboxes = page.locator('nylas-availability-picker >> .availability-day input[type="checkbox"]');

    // Verify each day's checked state
    await expect(dayCheckboxes.nth(0)).not.toBeChecked(); // Sunday should be unchecked
    await expect(dayCheckboxes.nth(1)).toBeChecked(); // Monday should be checked
    await expect(dayCheckboxes.nth(2)).toBeChecked(); // Tuesday should be checked
    await expect(dayCheckboxes.nth(3)).toBeChecked(); // Wednesday should be checked
    await expect(dayCheckboxes.nth(4)).toBeChecked(); // Thursday should be checked
    await expect(dayCheckboxes.nth(5)).toBeChecked(); // Friday should be checked
    await expect(dayCheckboxes.nth(6)).not.toBeChecked(); // Saturday should be unchecked
  });

  test('should display time inputs', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await waitForComponentReady(page);

    // Use shadow DOM selector
    const timeInputs = page.locator('nylas-availability-picker >> .time-picker input');
    await expect(timeInputs.first()).toBeVisible();
  });

  test('should display and allow timezone selection', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await waitForComponentReady(page);

    // Check timezone selector is visible
    const timezoneSelector = page.locator('nylas-availability-picker >> .select-timezone');
    await expect(timezoneSelector).toBeVisible();

    // Check current timezone button
    const timezoneButton = page.locator('nylas-availability-picker >> .select-timezone select-dropdown .dropdown button .selected-option');
    await expect(timezoneButton).toBeVisible();

    // Click to open dropdown
    await timezoneButton.click();

    // Type to search for a timezone
    const timezoneInput = page.locator('nylas-availability-picker >> .select-timezone select-dropdown .dropdown-content input[type="text"]');
    await timezoneInput.type('Vancouver');

    // Select the first matching timezone
    const timezoneLi = page.locator('nylas-availability-picker >> .select-timezone select-dropdown .dropdown-content ul li').first();
    await timezoneLi.click();

    // Verify selection
    await expect(timezoneButton).toContainText('America / Vancouver');
  });

  test('should allow selecting and unselecting days', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await waitForComponentReady(page);

    // Get Sunday checkbox (first day, initially unchecked)
    const sundayCheckbox = page.locator('nylas-availability-picker >> .availability-day input[type="checkbox"]').first();

    // Initially should be unchecked
    await expect(sundayCheckbox).not.toBeChecked();

    // Click to select Sunday
    await sundayCheckbox.click();
    await expect(sundayCheckbox).toBeChecked();

    // Click again to unselect
    await sundayCheckbox.click();
    await expect(sundayCheckbox).not.toBeChecked();
  });

  test('should allow updating time ranges', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);

    await waitForComponentReady(page);

    // Get Monday's (second day) start time input
    const mondayStartTime = page
      .locator('nylas-availability-picker >> .availability-day')
      .nth(1)
      .locator('.time-ranges .time-range nylas-time-window-picker[id="MON:0_start"] .time-picker input');

    // Check initial value
    await expect(mondayStartTime).toHaveValue('09:00am');

    // Click to open time picker and wait for dropdown
    await mondayStartTime.click();
    await page.waitForSelector('.time-picker .times');

    // Select a new time from dropdown
    const timeOption = page.locator('.time-picker .times li').filter({ hasText: '09:15am' });
    await timeOption.click();
    await expect(mondayStartTime).toHaveValue('09:15am');

    // Test manual time entry
    await mondayStartTime.click();
    await mondayStartTime.fill('01:00pm');
    const pmTimeOption = page.locator('.time-picker .times li').filter({ hasText: '01:00pm' });
    await pmTimeOption.click();
    await expect(mondayStartTime).toHaveValue('01:00pm');
  });

  test('should have correct default time ranges', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);
    await waitForComponentReady(page);

    // Check Monday's default time range
    const mondayStartTime = page
      .locator('nylas-availability-picker >> .availability-day')
      .nth(1)
      .locator('.time-ranges .time-range nylas-time-window-picker[id="MON:0_start"] .time-picker input');
    const mondayEndTime = page
      .locator('nylas-availability-picker >> .availability-day')
      .nth(1)
      .locator('.time-ranges .time-range nylas-time-window-picker[id="MON:0_end"] .time-picker input');

    await expect(mondayStartTime).toHaveValue('09:00am');
    await expect(mondayEndTime).toHaveValue('05:00pm');
  });

  test('should allow adding and removing time ranges', async ({ page }) => {
    await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);
    await waitForComponentReady(page);

    // Get Monday's container
    const monday = page.locator('nylas-availability-picker >> .availability-day').nth(1);
    await monday.waitFor({ state: 'visible' });

    // Initial count should be 1
    await expect(monday.locator('.time-range')).toHaveCount(1);

    // Find and click the add button
    const addButton = monday.locator('button[part="nap__add-time-range"]');
    await addButton.waitFor({ state: 'visible' });
    await addButton.click();

    // Wait for new time range
    await expect(monday.locator('.time-range')).toHaveCount(2);

    // Find and click the remove button
    const removeButton = monday.locator('.time-range').nth(1).locator('button:has(close-icon)');
    await removeButton.waitFor({ state: 'visible' });
    await removeButton.click();

    // Verify removal
    await expect(monday.locator('.time-range')).toHaveCount(1);
  });

  test('should update form value when changes are made', async ({ page }) => {
    await page.setContent(`
      <form id="availability-picker">
        <nylas-availability-picker name="availability"></nylas-availability-picker>
      </form>
    `);
    await waitForComponentReady(page);

    // Get form and component
    const form = page.locator('#availability-picker');
    const picker = page.locator('nylas-availability-picker');

    // Wait for initial render and form setup
    await page.waitForTimeout(1000);

    // Toggle Sunday (first day)
    const sundayCheckbox = picker.locator('.availability-day input[type="checkbox"]').first();
    await sundayCheckbox.waitFor({ state: 'visible' });
    await sundayCheckbox.click();

    // Wait for form update
    await page.waitForTimeout(1000);

    // Get form data
    const formData = await form.evaluate(form => {
      const data = new FormData(form as HTMLFormElement).get('availability');
      try {
        const parsed = data ? JSON.parse(data as string) : null;
        return parsed;
      } catch (e) {
        return null;
      }
    });

    // Verify form data
    expect(formData).toBeTruthy();
    expect(formData.openHours).toBeDefined();
    expect(formData.openHours.some(h => h.days.includes(0))).toBe(true); // Sunday is index 0
  });

  // TODO: Fix, the component itself is not behaving as expected

  // test('should validate time ranges', async ({ page }) => {
  //   await page.setContent(`<nylas-availability-picker></nylas-availability-picker>`);
  //   await waitForComponentReady(page);

  //   // Get a list of monday's start times, in the dropdown click 12:00pm, don't fill
  //   const monday = page.locator('nylas-availability-picker >> .availability-day').nth(1);
  //   const startTime = monday.locator('nylas-time-window-picker[id="MON:0_start"] .time-picker input');
  //   await startTime.waitFor({ state: 'visible' });
  //   await startTime.click();

  //   // Find the 12:00pm option in the dropdown
  //   const startTimeOption = page.locator('.time-picker .times li').filter({ hasText: '12:00pm' });
  //   await startTimeOption.click();

  //   // Wait for validation and check error state
  //   await page.waitForTimeout(1000);

  //   // Get a list of monday's end times, in the dropdown click 9:00am, don't fill
  //   const endTime = monday.locator('nylas-time-window-picker[id="MON:0_end"] .time-picker input');
  //   await endTime.waitFor({ state: 'visible' });

  //   // Fill in the 9:00am option in the dropdown
  //   await endTime.click();
  //   await endTime.fill('09:00am');

  //   // Wait for validation and check error state
  //   await page.waitForTimeout(1000);

  //   // Check that error message is displayed
  //   const errorMessage = monday.locator('.error');
  //   await expect(errorMessage).toBeVisible();
  //   await expect(errorMessage).toHaveText('End time must be after start time');
  // });
});
